import { useRef, useEffect, useCallback } from 'react';

/**
 * Hook for syncing scroll position with section selection and URL/state updates.
 * 
 * @param {Object} config - Configuration object
 * @param {string[]} config.sections - Ordered array of section identifiers
 * @param {string} config.selectedSection - Currently selected section ID
 * @param {Function} config.onSectionChange - Callback when scroll detects new section: (sectionId, { replace }) => void
 * @param {React.RefObject} config.headerRef - Ref to sticky header element
 * @param {Object} config.options - Optional configuration
 * @param {number} config.options.offset - Additional offset beyond header height (default: 20)
 * @param {number} config.options.scrollTimeout - Time to wait after scroll before re-enabling listener (default: 1000)
 * @returns {Object} - { sectionRef: (sectionId) => (element) => void, scrollToSection: (sectionId) => void }
 */
export function useScrollToSection({
  sections,
  selectedSection,
  onSectionChange,
  headerRef,
  options = {},
}) {
  const { offset = 20, scrollTimeout = 1000 } = options;

  const sectionRefs = useRef({});
  const selectedSectionRef = useRef(selectedSection);
  const isScrolling = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    selectedSectionRef.current = selectedSection;
  }, [selectedSection]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    if (!headerHeight) return;

    const handleScroll = () => {
      if (isScrolling.current) return;

      const threshold = headerHeight + offset;
      let currentSection = null;

      for (const sectionId of sections) {
        const ref = sectionRefs.current[sectionId];
        if (!ref) continue;

        const rect = ref.getBoundingClientRect();
        if (rect.top <= threshold) {
          currentSection = sectionId;
        }
      }

      if (currentSection && currentSection !== selectedSectionRef.current) {
        isInternalChange.current = true;
        onSectionChange(currentSection, { replace: true });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, headerRef, offset, onSectionChange]);

  const scrollToSection = useCallback((sectionId) => {
    const ref = sectionRefs.current[sectionId];
    if (!ref) return;

    const headerHeight = headerRef.current?.offsetHeight || 0;        
    const elementTop = ref.getBoundingClientRect().top + window.scrollY;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    isScrolling.current = true;
    window.scrollTo({
      top: elementTop - headerHeight,
      behavior: 'smooth',
    });

    scrollTimeoutRef.current = setTimeout(() => {
      isScrolling.current = false;
    }, scrollTimeout);
  }, [headerRef, scrollTimeout]);

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    scrollToSection(selectedSection);
  }, [selectedSection, scrollToSection]);

  const sectionRef = useCallback((sectionId) => {
    return (element) => {
      sectionRefs.current[sectionId] = element;
    };
  }, []);

  return {
    sectionRef,
    scrollToSection,
  };
}

